import { Injectable, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, Subscription, fromEvent, debounceTime } from 'rxjs';

/**
 * Сервис адаптивного масштабирования приложения.
 *
 * Стратегия: CSS zoom пропорционально масштабирует ВСЁ — текст, кнопки,
 * таблицы, отступы — сохраняя визуальную гармонию при любом размере окна.
 *
 * Базовая ширина (100%) = 1920px.
 * Минимальный zoom = 0.7 (при окне ~1344px) — текст остаётся читаемым.
 * CSS-переменные --scale и --scale-inv позволяют CSS-коду
 * компенсировать viewport-зависимые единицы (vh/vw).
 */
@Injectable({ providedIn: 'root' })
export class ViewportScaleService implements OnDestroy {
  /** Текущий zoom-коэффициент (0.7 … 1.0) */
  readonly scale$ = new BehaviorSubject<number>(1);

  /** Текущий BreakpointObserver-тег для шаблонов */
  readonly breakpoint$ = new BehaviorSubject<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('xl');

  private resizeSub: Subscription;
  private bpSub: Subscription;
  private scaleSub: Subscription;

  /* Точки разрыва */
  private static BP = {
    xs: '(max-width: 599px)',
    sm: '(min-width: 600px) and (max-width: 959px)',
    md: '(min-width: 960px) and (max-width: 1279px)',
    lg: '(min-width: 1280px) and (max-width: 1919px)',
    xl: '(min-width: 1920px)',
  };

  /* Настройки масштабирования */
  private readonly BASE_WIDTH = 1920;
  private readonly MIN_SCALE = 0.7;
  private readonly MAX_SCALE = 1;

  constructor(private bpObserver: BreakpointObserver) {
    this.recalc();
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(80))
      .subscribe(() => this.recalc());

    this.bpSub = this.bpObserver
      .observe(Object.values(ViewportScaleService.BP))
      .subscribe((result) => {
        for (const [key, query] of Object.entries(ViewportScaleService.BP)) {
          if (result.breakpoints[query]) {
            this.breakpoint$.next(key as any);
            break;
          }
        }
      });

    this.scaleSub = this.scale$.subscribe((s) => {
      this.applyToOverlay(s);
      const inv = Math.round((1 / s) * 1000) / 1000;
      document.documentElement.style.setProperty('--scale', `${s}`);
      document.documentElement.style.setProperty('--scale-inv', `${inv}`);
    });
    this.observeOverlayContainer();
  }

  /** Применить zoom к элементу (обычно document.documentElement) */
  applyTo(el: HTMLElement): void {
    this.scale$.subscribe((s) => {
      el.style.zoom = `${s}`;
    });
  }

  private overlayEl: HTMLElement | null = null;
  private overlayObserver: MutationObserver | null = null;

  /** Применить zoom к CDK overlay container */
  private applyToOverlay(scale: number): void {
    if (!this.overlayEl) {
      this.overlayEl = document.querySelector('.cdk-overlay-container') as HTMLElement;
    }
    if (this.overlayEl) {
      this.overlayEl.style.zoom = `${scale}`;
    }
  }

  /** Отслеживаем появление .cdk-overlay-container в DOM */
  private observeOverlayContainer(): void {
    if (this.overlayEl) return; // уже найден
    this.overlayObserver = new MutationObserver(() => {
      const el = document.querySelector('.cdk-overlay-container') as HTMLElement;
      if (el) {
        this.overlayEl = el;
        el.style.zoom = `${this.scale$.value}`;
        this.overlayObserver?.disconnect();
        this.overlayObserver = null;
      }
    });
    this.overlayObserver.observe(document.body, { childList: true });
  }

  private recalc(): void {
    const w = window.innerWidth;
    const raw = w / this.BASE_WIDTH;
    const clamped = Math.min(this.MAX_SCALE, Math.max(this.MIN_SCALE, raw));
    // Округляем до 2 знаков
    const rounded = Math.round(clamped * 100) / 100;
    if (rounded !== this.scale$.value) {
      this.scale$.next(rounded);
    }
  }

  ngOnDestroy(): void {
    this.resizeSub?.unsubscribe();
    this.bpSub?.unsubscribe();
    this.scaleSub?.unsubscribe();
    this.overlayObserver?.disconnect();
  }
}
